import React, { useCallback, useState } from "react";
import { ReviewOverviewSnapshot, ReviewServices, ReviewSnapshotParams } from "@/services/bedrock";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  exportSectionedDataToCsv,
  exportToXlsx,
  type XlsxColumnDef,
  type XlsxTableSection,
} from "@/utils/xlsxExportHelpers";
import { positiveSentimentPercent, replyRateFromSnapshot } from "./snapshotHelpers";
import { buildReputationReportMeta, formatPlayersExportFilters } from "../reportExportHelpers";
import { ReputationExportMenu } from "../ReputationExportMenu";
import { useSocialCoachSession } from "@/utils";

const EXPORT_PAGE_SIZE = 100;

type PlayerExportRow = {
  Player: string;
  Email: string;
  Reviews: number;
  "Star avg": number | "";
  "NPS avg": number | "";
  "10-pt avg": number | "";
  Sentiment: number | "";
  Replies: number | "";
};

const PLAYER_COLUMNS: XlsxColumnDef[] = [
  { key: "Player", header: "Player", type: "string", wch: 28 },
  { key: "Email", header: "Email", type: "string", wch: 28 },
  { key: "Reviews", header: "Reviews", type: "integer" },
  { key: "Star avg", header: "Star avg", type: "decimal" },
  { key: "NPS avg", header: "NPS avg", type: "decimal" },
  { key: "10-pt avg", header: "10-pt avg", type: "decimal" },
  { key: "Sentiment", header: "Sentiment", type: "percent" },
  { key: "Replies", header: "Replies", type: "percent" },
];

type PlayersExportButtonProps = {
  toQueryParams: () => ReviewSnapshotParams;
  filterLabels?: string[];
  totalCount?: number | null;
  loading?: boolean;
  disabled?: boolean;
};

function formatCountLabel(count: number): string {
  if (count === 1) return "1 Player";
  return `${count.toLocaleString()} Players`;
}

function transformSnapshotForExport(snapshot: ReviewOverviewSnapshot): PlayerExportRow {
  const sentiment = positiveSentimentPercent(snapshot);
  const replies = replyRateFromSnapshot(snapshot);

  return {
    Player: snapshot.player?.fullName || "Unknown Player",
    Email: snapshot.player?.email || "",
    Reviews: snapshot.totalReviews ?? 0,
    "Star avg": snapshot.totalStarReviews > 0 ? Number(snapshot.avgStarRating) : "",
    "NPS avg": snapshot.totalNpsResponses > 0 ? Number(snapshot.avgNpsScore) : "",
    "10-pt avg": snapshot.totalTenPointResponses > 0 ? Number(snapshot.avgTenPointScore) : "",
    Sentiment: sentiment != null ? sentiment / 100 : "",
    Replies: replies != null ? replies / 100 : "",
  };
}

export function PlayersExportButton({
  toQueryParams,
  filterLabels,
  totalCount,
  loading = false,
  disabled,
}: PlayersExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { session } = useSocialCoachSession();
  const companyName = session?.companyName;
  const hasPlayers = (totalCount ?? 0) > 0;
  const label = totalCount == null ? "Export Players" : `Export ${formatCountLabel(totalCount)}`;

  const fetchAllSnapshotsForExport = useCallback(async () => {
    const { page: _page, perPage: _perPage, ...filterParams } = toQueryParams();
    const allSnapshots: ReviewOverviewSnapshot[] = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages) {
      const result = await ReviewServices.getReviewSnapshots({
        ...filterParams,
        entityScope: "players",
        page,
        perPage: EXPORT_PAGE_SIZE,
      });
      allSnapshots.push(...(result.content ?? []).filter(snapshot => snapshot.playerId != null));
      totalPages = result.pagination?.totalPages ?? 1;
      page += 1;
    }

    return allSnapshots;
  }, [toQueryParams]);

  const handleExport = useCallback(
    async (formatType: "xlsx" | "csv") => {
      setIsExporting(true);
      try {
        const params = toQueryParams();
        const allSnapshots = await fetchAllSnapshotsForExport();
        if (!allSnapshots.length) {
          toast.info("No Players to export");
          return;
        }

        const exportData = allSnapshots.map(transformSnapshotForExport);
        const section: XlsxTableSection = {
          title: "Player performance",
          columns: PLAYER_COLUMNS,
          rows: exportData,
        };
        const meta = buildReputationReportMeta({
          reportName: "SocialCoach Player Performance Report",
          description: "Player-level reputation metrics for the selected overview period and scope.",
          companyName,
          filters: filterLabels ?? formatPlayersExportFilters(params),
          totalRecords: exportData.length,
        });
        const filename = `Player_Performance_${format(new Date(), "yyyy-MM-dd")}`;

        if (formatType === "csv") {
          exportSectionedDataToCsv(filename, meta, [section]);
        } else {
          await exportToXlsx(filename, [{ name: "Players", meta, sections: [section] }]);
        }

        toast.success(`Exported ${exportData.length} Player${exportData.length === 1 ? "" : "s"}`);
      } catch {
        toast.error("Failed to export Players. Please try again.");
      } finally {
        setIsExporting(false);
      }
    },
    [companyName, fetchAllSnapshotsForExport, filterLabels, toQueryParams]
  );

  return (
    <ReputationExportMenu
      label={label}
      isExporting={isExporting}
      disabled={disabled || loading || !hasPlayers}
      onExport={handleExport}
    />
  );
}
